import axios from "axios";

const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

const getToken = () => localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error.response || error.message);
    return Promise.reject(error);
  }
);

const getCacheKey = (url, params = {}) => {
  const paramString = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join("&");
  return `${url}${paramString ? `?${paramString}` : ""}`;
};

const clearCachePattern = (pattern) => {
  for (const key of requestCache.keys()) {
    if (key.includes(pattern)) {
      requestCache.delete(key);
    }
  }
};

export const fetchDataFromApi = async (url, useCache = true) => {
  try {
    const cacheKey = getCacheKey(url);

    if (useCache && requestCache.has(cacheKey)) {
      const cachedData = requestCache.get(cacheKey);
      if (Date.now() - cachedData.timestamp < CACHE_DURATION) {

        return cachedData.data;
      } else {
        requestCache.delete(cacheKey);
      }
    }



    const { data } = await api.get(url);

    if (useCache) {
      requestCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
    }

    return data;
  } catch (error) {
    // Enhanced error logging
    console.error("Fetch error details:", {
      url: url,
      fullUrl: process.env.REACT_APP_BASE_URL + url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      headers: error.response?.headers
    });

    // Consider throwing the error instead of returning null
    // throw error;
    return null;
  }
};


// Fixed postData function in utils/api.js
export const postData = async (url, payload) => {
  try {


    const { data } = await api.post(url, payload);

    clearCachePattern(url.split("/")[1]);

    return data;
  } catch (error) {
    console.error("Post data error:", error);

    // Return error response instead of throwing
    if (error.response && error.response.data) {
      // Return the server's error response
      return {
        error: true,
        msg: error.response.data.msg || error.response.data.message || "Server error",
        status: error.response.status
      };
    } else if (error.request) {
      // Network error
      return {
        error: true,
        msg: "Network error. Please check your connection.",
        status: 0
      };
    } else {
      // Other error
      return {
        error: true,
        msg: error.message || "Something went wrong",
        status: 0
      };
    }
  }
};
export const putData = async (url, payload) => {
  try {
    const { data } = await api.put(url, payload);

    clearCachePattern(url.split("/")[1]);

    return data;
  } catch (error) {
    console.error("Put data error:", error.response || error.message);
    throw error;
  }
};

export const deleteData = async (url) => {
  try {
    const { data } = await api.delete(url);

    clearCachePattern(url.split("/")[1]);

    return data;
  } catch (error) {
    console.error("Delete data error:", error.response || error.message);
    throw error;
  }
};

export const batchDeleteData = async (urls) => {
  try {
    const deletePromises = urls.map((url) => api.delete(url));
    const results = await Promise.allSettled(deletePromises);

    urls.forEach((url) => clearCachePattern(url.split("/")[1]));

    return results;
  } catch (error) {
    console.error("Batch delete error:", error);
    throw error;
  }
};

export const uploadImage = async (url, formData) => {
  try {
    const { data } = await api.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    clearCachePattern(url.split("/")[1]);

    return data;
  } catch (error) {
    console.error("Image upload error:", error.response || error.message);
    throw error;
  }
};

export const deleteImages = async (url) => {
  try {
    const { data } = await api.delete(url);
    clearCachePattern(url.split("/")[1]);
    return data;
  } catch (error) {
    console.error("Delete image error:", error.response || error.message);
    throw error;
  }
};

export const clearCache = () => {
  requestCache.clear();

};

export const getCacheSize = () => requestCache.size;

export const updateCartItem = async (itemId, updates) => {
  try {
    const { data } = await api.put(`/api/cart/${itemId}`, updates);
    clearCachePattern("cart");
    return data;
  } catch (error) {
    console.error("Cart update error:", error);
    throw error;
  }
};

const updateTimeouts = new Map();

export const debouncedCartUpdate = (itemId, updates, delay = 500) => {
  return new Promise((resolve, reject) => {
    if (updateTimeouts.has(itemId)) {
      clearTimeout(updateTimeouts.get(itemId));
    }

    const timeoutId = setTimeout(async () => {
      try {
        const result = await updateCartItem(itemId, updates);
        updateTimeouts.delete(itemId);
        resolve(result);
      } catch (error) {
        updateTimeouts.delete(itemId);
        reject(error);
      }
    }, delay);

    updateTimeouts.set(itemId, timeoutId);
  });
};
